import { useEffect, useState } from 'react'
import { Badge, Modal, Text } from '@mantine/core'
import { usePinnedAutoScroll } from '@/hooks/usePinnedAutoScroll'
import { isLiveRunStatus, runDuration, RUN_STATUS_COLORS, type TaskRunInfo } from './hooks'

interface RunLogModalProps {
  run: TaskRunInfo
  opened: boolean
  onClose: () => void
}

/**
 * Scrollable run log. Follows appended log lines only while the user is pinned to the
 * bottom (the BeAssistant chat behavior via usePinnedAutoScroll); the parent keeps the
 * run polling while it's live, so the log and status refresh on their own.
 */
export function RunLogModal({ run, opened, onClose }: RunLogModalProps) {
  const live = isLiveRunStatus(run.status)
  const { bottomRef, scrollContainerRef, onScroll } = usePinnedAutoScroll([run.log])

  // Re-render every second while open on a live run so the "active for" duration ticks.
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!opened || !live) return
    const timer = setInterval(() => setTick((tick) => tick + 1), 1_000)
    return () => clearInterval(timer)
  }, [opened, live])

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="80%"
      title={
        <div className="flex items-center gap-3">
          <Text fw={600}>
            Run #{run.id} <span className="font-mono text-sm text-gray-500">{run.slug}</span>
          </Text>
          <Badge color={RUN_STATUS_COLORS[run.status]} variant="light">
            {run.status}
          </Badge>
          <Text size="sm" c="dimmed">
            {run.startedAt
              ? `${live ? 'active for' : 'ran for'} ${runDuration(run)}`
              : 'not started'}
          </Text>
        </div>
      }
    >
      <div
        ref={scrollContainerRef}
        onScroll={onScroll}
        className="h-[65vh] overflow-y-auto rounded bg-gray-900 p-3 font-mono text-xs whitespace-pre-wrap text-gray-100"
      >
        {run.log ? run.log : <span className="text-gray-400">No log output yet.</span>}
        <div ref={bottomRef} />
      </div>
    </Modal>
  )
}
